import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRadium, DetectCompact, SearchParams } from 'higher-order-components'

import {
  ServiceMainPanel,
  ServiceListInspect,
  ServiceListSelect,
  HeadingBlock
} from 'containers'

import { Button, Heading } from 'components'

import COLOR_PALETTE from 'constants/color-palette'
import { get } from 'utils/object'
import { H4, P, Interactive } from 'AtomicComponents';

export class ServiceDetails extends Component {
  static propTypes = {
    data: PropTypes.object,
    factoryCode: PropTypes.string,
    serviceDetailsURL: PropTypes.string,
    selectAdditionalItems: PropTypes.bool,
    scheduledServiceItemsLimit: PropTypes.number,
    compact: PropTypes.bool,
    search: PropTypes.object,
    updateSelectedAddtionalServices: PropTypes.func
  }

  static defaultProps = {
    data: {},
    factoryCode: '',
    serviceDetailsURL: '',
    selectAdditionalItems: false,
    compact: true
  }

  constructor(props) {
    super(props)
    this.state = {
      activeServiceCodeIndex: 0,
      selectedAdditionalServices: {}
    }

    this.services = props.data.serviceItems
      .filter(s =>
        s.factoryCodeEntity.type === 'Scheduled Maintenance' && s.factoryCodeEntity.distance > 0)
      .sort((s1, s2) => s1.factoryCodeEntity.distance - s2.factoryCodeEntity.distance)
      .slice(0, props.scheduledServiceItemsLimit > 0 ? props.scheduledServiceItemsLimit : 1e10)
      .map((s) => {
        const m = /([^\s]+) Service or (.+)/.exec(s.factoryCodeEntity.description)
        return {
          name: m ? `${m[1]} Service | $${s.basePrice}` : s.factoryCodeEntity.description,
          extraLabel: m ? `or ${m[2]}` : '',
          price: `$${s.basePrice}`,
          basePrice: s.basePrice,
          shortName: m ? `${m[1]} Service` : s.factoryCodeEntity.description,
          code: s.factoryCodeEntity.code,
          item: s
        }
      })

    this.additionalServices = this.props.data.serviceItems
      .filter(s => s.factoryCodeEntity.type.match(/^Additional/))
      .map(s => ({
        name: s.factoryCodeEntity.description,
        description:
          s.operationActions.length > 0 ? s.operationActions[0].operations[0].description : '',
        price: `$${s.basePrice}`,
        basePrice: s.basePrice,
        code: s.factoryCodeEntity.code
      }))

    if (props.factoryCode) {
      this.state.activeServiceCodeIndex = this.services.findIndex(s => s.code === props.factoryCode)
    }

    this.toggleService = (code) => {
      const newSelectedAdditionalServices = {
        ...this.state.selectedAdditionalServices
      }
      newSelectedAdditionalServices[code] = !newSelectedAdditionalServices[code]
      this.syncAdditionalServicesState(newSelectedAdditionalServices)
    }
  }

  calculateTotal() {
    const activeService = this.services[this.state.activeServiceCodeIndex]
    const totalPrice = this.additionalServices
      .filter(s => this.state.selectedAdditionalServices[s.code])
      .reduce((price, s) => price + s.basePrice, activeService.basePrice)
    return `${totalPrice.toFixed(0)}`
  }

  componentDidMount() {
    const { asmc } = get('search.query')(this.props)
    const asmcArray = asmc && (Array.isArray(asmc) ? asmc : [asmc])
    const preSelectedAdditionalItems = {}
    asmcArray &&
      asmcArray.forEach((item) => {
        preSelectedAdditionalItems[item] = true
      })
    this.syncAdditionalServicesState(preSelectedAdditionalItems)
  }

  componentWillReceiveProps(newProps) {
    if (newProps.factoryCode !== this.props.factoryCode) {
      this.setState({
        activeServiceCodeIndex: this.services.findIndex(s => s.code === newProps.factoryCode),
        selectedAdditionalServices: {}
      })
    }
  }

  syncAdditionalServicesState = (newSelectedAdditionalItems) => {
    const { updateSelectedAddtionalServices } = this.props
    // When we updae the selected additional services here, we also need to notify the wrapper (calculator) to update it's state
    this.setState({ selectedAdditionalServices: newSelectedAdditionalItems })
    updateSelectedAddtionalServices && updateSelectedAddtionalServices(newSelectedAdditionalItems)
  }

  getActiveService() {
    return this.services[this.state.activeServiceCodeIndex]
  }

  getFullDetailsURL() {
    const activeService = this.getActiveService()
    if (!activeService) return ''

    const vin = this.props.data.vehicle ? this.props.data.vehicle.vin : ''
    const selectedCodes = Object.keys(this.state.selectedAdditionalServices)
      .filter(k => this.state.selectedAdditionalServices[k])
      .join(',')
    const vehicleParams =
      vin && vin.length > 0
        ? `&VIN=${vin}&modelGroup=${encodeURIComponent(this.props.data.vehicle.modelGroup)}&rego=${this.props.data.vehicle.rego}`
        : ''
    const additionalCodesParam = selectedCodes.length > 0 ? `&additionalCodes=${selectedCodes}` : ''
    const responseUrl = `${this.props.serviceDetailsURL || '/service-details'}?serviceSpecCode=${this
      .props.data
      .serviceSpecCode}&factoryCode=${activeService.code}${vehicleParams}${additionalCodesParam}`
    return responseUrl
  }

  isSPG() {
    try {
      // trying to guess MSS vs SPG service type
      // MSS factoryCode starts with 'S' and SPG starts with 'M' (yes, it's counterintuitive)
      return this.props.data && this.props.data.serviceItems.some(si => si.factoryCode[0] === 'M')
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.log(e)
      return false
    }
  }

  renderAdditionalDetails() {
    const { selectAdditionalItems: selectable, compact } = this.props
    const additionalItemsHeading = this.isSPG()
      ? 'Optional Service Maintenance items'
      : 'ADDITIONAL SCHEDULED MAINTENANCE'

    return (
      <div style={styles.additionalServicesContainer(compact)}>
        {this.additionalServices &&
          !!this.additionalServices.length && (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  borderBottom: compact && '1px solid #aaa',
                  marginBottom: '1em'
                }}>
                <div style={{ padding: '4em 0px 0.2em' }}>
                  <H4 withDetails>{additionalItemsHeading}</H4>
                  <P>
                    Items below must be attended to when they are due.
                  </P>
                </div>
                {selectable && (
                  <div
                    style={{
                      color: COLOR_PALETTE.BLACK,
                      fontStyle: 'italic',
                      marginBottom: '1.3em',
                      fontSize: '90%',
                      fontWeight: 200
                    }}>
                    Select to add to your Total Service Price
                  </div>
                )}
                {selectable ? (
                  <ServiceListSelect
                    services={this.additionalServices}
                    selectedServices={this.state.selectedAdditionalServices}
                    onServiceClick={code => this.toggleService(code)}
                    compact={compact} />
                ) : (
                  <ServiceListInspect services={this.additionalServices} compact={compact} />
                )}
              </div>
              {compact && (
                <Button
                  styleName="blackOutlined"
                  label="View item details"
                  href={this.getFullDetailsURL().concat('#item-details')}
                  fullWidth
                  inline />
              )}
            </div>
          )}
        {selectable && (
          <div style={{ width: '100%' }}>
            <div style={styles.totalServicePrice(compact)}>
              Total service price
              <div style={{ marginTop: '.5em' }}>
                <span style={styles.symbols(compact)}>$</span>
                <span style={styles.totalServicePriceValue(compact)}>{this.calculateTotal()}</span>
                <span style={styles.symbols(compact)}>*</span>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  _findNonZeroDistance = () =>{
    let distance = 0
    this.props.data && this.props.data.serviceItems.forEach(item=>{
      if (item.factoryCodeEntity.distance > 0 && distance === 0 ){
        distance = item.factoryCodeEntity.distance
      }
    })
    return distance
  }

  render() {
    const activeService = this.getActiveService()
    if (!activeService) return null
    // /owners/servicing/owners-service-calculator/?VIN=MM0UP0YF100105709&factoryCode=S020
    // /owners/servicing/mazda-genuine-service/?body=Wagon&engine=2.2l%20Diesel%20Auto%20FWD&grade=Touring&model=Mazda6
    const { compact } = this.props
    const spg = this.isSPG()
    const baseScheduleHeading = spg ? 'Scheduled Maintenance' : 'Base Scheduled Maintenance'
    const km = this._findNonZeroDistance().toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    const baseScheduleDescription = spg
      ? `Regular servicing is the key to maintaining your investment, which is why it’s important you return every ${km} kms. To ensure your car receives the best and most appropriate service schedule, you should consult your Mazda Dealer.`
      : `Base scheduled maintenance is due every ${km} km, but no longer than 12 months, whichever comes first.`

    return (
      <div style={styles.base}>
        <div
          style={{
            width: '100%'
          }}>
          <H4 withDetails>{baseScheduleHeading}</H4>
          <P>{baseScheduleDescription}</P>
        </div>
        {this.props.factoryCode && (
          <div
            style={{
              paddingTop: '2em',
              borderBottom: '1px solid rgb(203, 203, 203)',
              textAlign: "left",
              width: '100%'
            }}>
            <H4 withDetails>{activeService.shortName}</H4>
            <div style={styles.introOwner(compact)}>
              <Interactive>{activeService.extraLabel}</Interactive>
            </div>
          </div>
        )}
        <ServiceMainPanel
          services={this.services}
          activeService={activeService}
          fullDetailsURL={this.getFullDetailsURL()}
          factoryCode={this.props.factoryCode}
          activeServiceCodeIndex={this.state.activeServiceCodeIndex}
          compact={this.props.compact}
          onServiceSelect={(index) => {
            this.setState({ activeServiceCodeIndex: index })
          }} />
        {this.renderAdditionalDetails()}
      </div>
    )
  }
}

export default withRadium(
  DetectCompact(),
  SearchParams({ exposeParams: ['factoryCode', 'VIN', 'asmc'] })
)(ServiceDetails)

const styles = {
  base: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'white'
  },

  mainServiceHeader: {
    textAlign: 'left',
    width: '100%'
  },

  introOwner: compact => ({
    display: 'flex',
    flexDirection: compact ? 'column' : 'row',
    alignItems: 'baseline',
    padding: '0.4em 0 1em',
    justifyContent: 'space-between'
  }),

  additionalServicesContainer: compact => ({
    boxSizing: 'border-box',
    width: '100%',
    display: 'block',
    padding: 0
  }),

  totalServicePrice: compact => ({
    display: 'flex',
    flexDirection: compact ? 'column' : 'row',
    justifyContent: 'space-between',
    textTransform: 'uppercase',
    borderBottom: !compact && '1px solid #aaaaaa',
    fontSize: '112.5%',
    letterSpacing: '0.2em',
    fontFamily:
      'MazdaTypeTT, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    paddingTop: '2em',
    paddingBottom: '0.5em',
    alignItems: 'center',
    textAlign: compact && 'center'
  }),

  totalServicePriceValue: compact => ({
    paddingBottom: '0.2em',
    fontSize: compact ? '250%' : '125%',
    fontWeight: compact && '200',
    letterSpacing: '0'
  }),

  symbols: compact => ({
    display: compact && 'inline-block',
    verticalAlign: 'top',
    paddingTop: '.1em',
    letterSpacing: compact && '0',
    fontWeight: compact && '200',
    fontSize: compact && '140%'
  })
}



// WEBPACK FOOTER //
// ./src/containers/service-details/index.jsx